import { ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, timestamp } from 'rxjs';

import { InstitutionService } from '@services/institution.service';
import { LoadingService } from '@services/loading.service';
import { ReportService } from '@services/report.service';
import { PdfExportService, PdfColumn } from '@services/pdf-export.service';
import { CallsService } from '@services/api/calls.service';
import { LoggerService } from '@services/logger.service';

import { SelectOption } from '@interfaces/input-select-option.interface';
import { ReportClinicBillingResponse, ReportClinicBillingItem } from '@interfaces/report';

import { AddHiddenDataDialogComponent } from './add-hidden-data-dialog.component';

import { UserAction } from '@enums/user-action';

import moment from 'moment';
import { Utils } from '@json/src/app/Utils';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-report-billing-hospital',
  templateUrl: './report-billing-hospital.component.html'
})
export class ReportBillingHospitalComponent implements OnInit, OnDestroy {

  @ViewChild(MatSort) sort: MatSort;

  public form: FormGroup;
  public tableFormArray: FormArray;

  public selectOptionsInsitutions: SelectOption[];

  readonly maxDate = new Date(moment().year(), moment().month(), moment().date());
  maxDateFrom: Date;
  minDateTo: Date;

  ELEMENT_DATA: ReportClinicBillingItem[] = [];
  hiddenData: ReportClinicBillingItem[] = [];

  displayedColumns: string[] = ['FIPACIENTE', 'FICIAP', 'FIMEDICA', 'FIMPORTEC',  'IVA', 'FIUNIENV', 'FIUNIDIA', 'FIDIAS', 'FIMPORTEF', 'TIPOCOMPRA', 'ACCIONES'];
  dataSource = new MatTableDataSource<ReportClinicBillingItem>(this.ELEMENT_DATA);

  dataRequested: boolean = false;

  // Mapa para mantener la correspondencia entre elementos y sus FormGroups
  private rowFormMap = new Map<string, FormGroup>();

  private subs: Subscription[] = [];  
  
  constructor(
    private formBuilder: FormBuilder,
    private institutionService: InstitutionService,
    private loadingService: LoadingService,
    private reportService: ReportService,
    private cdRef: ChangeDetectorRef,
    private pdfExportService: PdfExportService,
    private calls: CallsService,
    private dialog: MatDialog,
    private logger: LoggerService
  ) {
    this.form = this.formBuilder.group({
      selectedInstitution: ['', Validators.required],
      fromDate: [moment(), Validators.required],
      toDate: [moment(), Validators.required],
      filter: [''],
      displayMode: [2],
    });

    this.tableFormArray = this.formBuilder.array([]);
    this.selectOptionsInsitutions = [];
  }

  get draftData(): any {
    if (!this.dataRequested || this.dataSource.data.length === 0) {
      return null;
    }

    // Sincronizar los datos del formulario con el dataSource antes de guardar
    this.syncFormWithDataSource();

    return {
      formData: {
        selectedInstitution: this.form.controls['selectedInstitution'].value,
        fromDate: this.form.controls['fromDate'].value?.format('YYYY-MM-DD'),
        toDate: this.form.controls['toDate'].value?.format('YYYY-MM-DD'),
        displayMode: this.form.controls['displayMode'].value,
      },
      tableData: this.dataSource.data,
      hiddenData: this.hiddenData,
      timestamp: new Date().toISOString()
    };
  }

  get draftDataId(): string {
    return 'report-billing-hospital-draft';
  }

  ngOnInit(): void {
    this.loadingService.start('Cargando datos...');

    // Inicializar maxDateFrom y minDateTo
    const toDateValue = this.form.controls['toDate'].value;
    const fromDateValue = this.form.controls['fromDate'].value;

    this.maxDateFrom = toDateValue ? toDateValue.toDate() : this.maxDate;
    this.minDateTo = fromDateValue ? fromDateValue.toDate() : null;

    // Configurar el filterPredicate personalizado para buscar en todos los campos relevantes
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const searchStr = filter.trim().toLowerCase();
      if (!searchStr) {
        return true;
      }

      // Obtener valores de CIPNA y CN desde FormControls si existen
      let cipnaValue = '';
      let cnValue = '';
      let cieValue = '';

      if (data._rowId && this.rowFormMap.has(data._rowId)) {
        const formGroup = this.rowFormMap.get(data._rowId);
        cipnaValue = (formGroup?.get('FIPACIENTE')?.value || data.FIPACIENTE || '').toString().toLowerCase();
        cnValue = (formGroup?.get('FIMEDICA')?.value || data.FIMEDICA || '').toString().toLowerCase();
        cieValue = (formGroup?.get('FICIAP')?.value || data.FICIAP || '').toString().toLowerCase();
      } else {
        cipnaValue = (data.FIPACIENTE || '').toString().toLowerCase();
        cnValue = (data.FIMEDICA || '').toString().toLowerCase();
        cieValue = (data.FICIAP || '').toString().toLowerCase();
      }

      // Obtener valores de nombres
      const nombrePacienteValue = (data.NombrePaciente || '').toString().toLowerCase();
      const nombreMedicamentoValue = (data.NombreMedicamento || '').toString().toLowerCase();

      // Buscar en TODOS los campos independientemente del displayMode
      return cipnaValue.includes(searchStr) ||
             nombrePacienteValue.includes(searchStr) ||
             cnValue.includes(searchStr) ||
             nombreMedicamentoValue.includes(searchStr) ||
             cieValue.includes(searchStr);
    };

    // Configurar el sortingDataAccessor para manejar el ordenamiento según displayMode
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      const displayMode = this.form.controls['displayMode'].value;

      // Para FIPACIENTE, ordenar por CIPNA o NombrePaciente según displayMode
      if (property === 'FIPACIENTE') {
        if (displayMode === 1) {
          // Modo numérico - ordenar por CIPNA (obtener del FormControl si existe)
          if (item._rowId && this.rowFormMap.has(item._rowId)) {
            const formGroup = this.rowFormMap.get(item._rowId);
            const value = formGroup?.get('FIPACIENTE')?.value;
            return value ?? item.FIPACIENTE ?? '';
          }
          return item.FIPACIENTE ?? '';
        } else {
          // Modo descriptivo - ordenar por nombre de paciente
          return (item.NombrePaciente ?? '').toLowerCase();
        }
      }

      // Para FIMEDICA, ordenar por CN o NombreMedicamento según displayMode
      if (property === 'FIMEDICA') {
        if (displayMode === 1) {
          // Modo numérico - ordenar por CN (obtener del FormControl si existe)
          if (item._rowId && this.rowFormMap.has(item._rowId)) {
            const formGroup = this.rowFormMap.get(item._rowId);
            const value = formGroup?.get('FIMEDICA')?.value;
            return value ?? item.FIMEDICA ?? 0;
          }
          return item.FIMEDICA ?? 0;
        } else {
          // Modo descriptivo - ordenar por nombre de medicamento
          return (item.NombreMedicamento ?? '').toLowerCase();
        }
      }

      // Para otras columnas, obtener el valor del FormControl si existe, sino del item
      if (item._rowId && this.rowFormMap.has(item._rowId)) {
        const formGroup = this.rowFormMap.get(item._rowId);
        const formValue = formGroup?.get(property)?.value;
        if (formValue !== undefined && formValue !== null) {
          return formValue;
        }
      }

      // Si no hay FormControl o no tiene valor, usar el valor del item
      const itemValue = item[property];
      return itemValue !== undefined && itemValue !== null ? itemValue : '';
    };

    this.institutionService.getInstitutions().then(() => {
      this.selectOptionsInsitutions = Object.assign([], this.institutionService.institutionList.getValue());
      this.selectOptionsInsitutions = this.selectOptionsInsitutions.sort((a, b) => a.label.localeCompare(b.label))

      if (this.selectOptionsInsitutions.length === 1) {
        this.form.controls['selectedInstitution'].setValue(this.selectOptionsInsitutions[0].value);
        this.form.controls['selectedInstitution'].disable();
      }

      this.loadingService.stop();
    });

    this.subs.push(
      this.form.controls['filter'].valueChanges.subscribe((value) => {
        this.dataSource.filter = value;
      }),
      // Actualizar maxDateFrom cuando cambie toDate
      this.form.controls['toDate'].valueChanges.subscribe((value) => {
        if (value) {
          this.maxDateFrom = value.toDate();
          // Si fromDate es posterior a toDate, ajustar fromDate
          const fromDate = this.form.controls['fromDate'].value;
          if (fromDate && fromDate.isAfter(value)) {
            this.form.controls['fromDate'].setValue(value);
          }
        }
      }),
      // Actualizar minDateTo cuando cambie fromDate
      this.form.controls['fromDate'].valueChanges.subscribe((value) => {
        if (value) {
          this.minDateTo = value.toDate();
          // Si toDate es anterior a fromDate, ajustar toDate
          const toDate = this.form.controls['toDate'].value;
          if (toDate && toDate.isBefore(value)) {
            this.form.controls['toDate'].setValue(value);
          }
        }
      }),
      // Re-ordenar y re-filtrar cuando cambie el displayMode
      this.form.controls['displayMode'].valueChanges.subscribe(() => {
        // Si hay un ordenamiento activo, forzar la actualización
        if (this.sort && this.sort.active) {
          this.dataSource.sort = this.sort;
        }
        // Forzar la re-evaluación del filtro
        const currentFilter = this.dataSource.filter;
        this.dataSource.filter = '';
        this.dataSource.filter = currentFilter;
      })
    );

    this.logger.logPage('Reporte: Facturación Clinic');
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  handleGeneratePDF(): void {
    if (this.dataSource.data.length === 0) {
      console.warn('No hay datos disponibles para exportar');
      return;
    }

    this.generatePDF();
  }

  handleGenerateExcel(): void {
    if (this.dataSource.data.length === 0) {
      console.warn('No hay datos disponibles para exportar');
      return;
    }

    this.generateExcel();
  }

  generatePDF(): void {
    // Sincronizar los datos del formulario con el dataSource antes de exportar
    this.syncFormWithDataSource();

    const institutionName = this.form.controls['selectedInstitution'].value?.name || '';
    const fromDate = this.form.controls['fromDate'].value.format('DD/MM/YYYY');
    const toDate = this.form.controls['toDate'].value.format('DD/MM/YYYY');

    // Preparar los datos con campos calculados para columnas combinadas
    const pdfData = this.dataSource.data.map(row => ({
      ...row,
      PacienteCompleto: row.NombrePaciente
        ? `(${row.FIPACIENTE}) ${row.NombrePaciente}`
        : row.FIPACIENTE || '',
      MedicamentoCompleto: row.NombreMedicamento
        ? `(${row.FIMEDICA}) ${row.NombreMedicamento}`
        : row.FIMEDICA || ''
    }));

    // Definir las columnas del PDF
    const columns: PdfColumn[] = [
      {
        header: '(CIPNA) Paciente',
        field: 'PacienteCompleto',
        width: '*',
        align: 'left'
      },
      {
        header: 'CIE',
        field: 'FICIAP',
        width: 'auto',
        align: 'center',
        bold: true
      },
      {
        header: '(CN) Medicamento',
        field: 'MedicamentoCompleto',
        width: '*',
        align: 'left'
      },
      {
        header: 'Precio',
        field: 'FIMPORTEC',
        width: 'auto',
        align: 'right',
        format: (value) => value != null ? value.toFixed(2) + '€' : '-'
      },
      {
        header: 'Precio IVA',
        field: 'FIMPORTECIVA',
        width: 'auto',
        align: 'right',
        format: (value) => value != null ? value.toFixed(2) + '€' : '-'
      },
      {
        header: 'U/envase',
        field: 'FIUNIENV',
        width: 'auto',
        align: 'center'
      },
      {
        header: 'U/día',
        field: 'FIUNIDIA',
        width: 'auto',
        align: 'center'
      },
      {
        header: 'Días trat.',
        field: 'FIDIAS',
        width: 'auto',
        align: 'center'
      },
      {
        header: 'IVA',
        field: 'IVA',
        width: 'auto',
        align: 'right',
        format: (value) => value != null ? Math.round(value) + '%' : '-'
      },
      {
        header: 'Imp. facturado',
        field: 'FIMPORTEF',
        width: 'auto',
        align: 'right',
        format: (value) => value != null ? value.toFixed(2) + '€' : '-'
      },
      {
        header: 'Tipo compra',
        field: 'TIPOCOMPRA',
        width: 'auto',
        align: 'center',
        format: (value) => value === 1 ? 'Lab' : 'Distribuidor'
      }
    ];

    // Configuración del PDF
    this.pdfExportService.exportToPdf({
      title: 'Reporte de Facturación',
      subtitle: institutionName,
      filename: `reporte-facturacion-${institutionName}-${moment().format('YYYY-MM-DD')}.pdf`,
      orientation: 'landscape',
      pageSize: 'A4',
      columns: columns,
      data: pdfData,
      metadata: [
        { label: 'Fecha desde', value: fromDate },
        { label: 'Fecha hasta', value: toDate },
        { label: 'Total registros', value: this.dataSource.data.length.toString() }
      ]
    });

    this.logger.logAction(UserAction.Click, {
      message: 'Exportar PDF',
      file: `reporte-facturacion-${institutionName}-${moment().format('YYYY-MM-DD')}.pdf`,
      records: this.dataSource.data.length
    });
  }

  generateExcel(): void {
    // Sincronizar los datos del formulario con el dataSource antes de exportar
    this.syncFormWithDataSource();

    // Definir las cabeceras del Excel
    const headers = [
      'FICENTRO',
      'FIMES',
      'FIAÑO',
      'FIPACIENTE',
      'FICIAP',
      'FIMEDICA',
      'FIMPORTEC',
      'FIMPORTECIVA',
      'FIUNIENV',
      'FIUNIDIA',
      'FIDIAS',
      'IVA',
      'FIMPORTEF',
      'TIPOCOMPRA',
      'NFACTURA'
    ];

    // Preparar los datos para Excel
    const excelData: any[] = [];

    // Agregar datos
    this.dataSource.data.forEach(row => {
      const rowData: any = {};
      headers.forEach(field => {
        const value = row[field as keyof ReportClinicBillingItem];
        rowData[field] = value !== undefined && value !== null ? value : '';
      });
      excelData.push(rowData);
    });

    // Crear el libro de trabajo (workbook)
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData, { header: headers });
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Reporte Facturación': worksheet },
      SheetNames: ['Reporte Facturación']
    };

    // Generar nombre del archivo con fecha actual
    const fromDate = this.form.controls['fromDate'].value.format('YYYY-MM-DD');
    const toDate = this.form.controls['toDate'].value.format('YYYY-MM-DD');
    const institutionName = this.form.controls['selectedInstitution'].value?.shortName || 'institucion';
    const filename = `reporte-facturacion-${institutionName}-${fromDate}_${toDate}.xlsx`;

    this.logger.logAction(UserAction.Click, {
      message: 'Exportar Excel',
      file: `reporte-facturacion-${institutionName}-${fromDate}_${toDate}.xlsx`,
      records: this.dataSource.data.length
    });

    // Exportar el archivo
    XLSX.writeFile(workbook, filename);
  }

  handleGenerateReport(): void {
    if (this.dataSource.data.length === 0) {
      console.warn('No hay datos disponibles para exportar');
      return;
    }

    this.generateCSV();
  }

  generateCSV(): void {
    // Sincronizar los datos del formulario con el dataSource antes de exportar
    this.syncFormWithDataSource();

    // Primera línea: Cabeceras en mayúsculas todo seguido
    // let txtContent = 'FICENTROFIMESFIAÑOFIPACIENTEFICIAPFIIMEDICAFIMPORTECFIUNIENVFIUNIDIAFIDIASFIMPORTEFTIPOCOMPRANFACTURA\n';
    let txtContent = '';

    // Helper para formatear campos con longitud fija
    const padField = (value: any, length: number, align: 'left' | 'right' = 'left', fillChar: string = ' '): string => {
      let strValue = value !== undefined && value !== null ? String(value) : '';

      // Truncar si es más largo
      if (strValue.length > length) {
        strValue = strValue.substring(0, length);
      }

      // Rellenar según alineación
      if (align === 'right') {
        return strValue.padStart(length, fillChar);
      } else {
        return strValue.padEnd(length, fillChar);
      }
    };

    // Helper para formatear números decimales (el punto cuenta como carácter)
    const formatDecimal = (value: any, totalLength: number, decimals: number): string => {
      const numValue = value !== undefined && value !== null ? Number(value) : 0;
      const formatted = numValue.toFixed(decimals); // Mantener el punto decimal
      return padField(formatted, totalLength, 'right', '0');
    };

    // Generar las líneas de datos
    this.dataSource.data.forEach(row => {
      let line = '';

      // FICENTRO - 5 caracteres, solo números
      line += padField(row.FICENTRO || '', 5, 'right', '0');

      // FIMES - 2 caracteres, solo números de 1-12
      line += padField(row.FIMES || '', 2, 'right', '0');

      // FIAÑO - 4 caracteres, solo números
      line += padField(row.FIAÑO || '', 4, 'right', '0');

      // FIPACIENTE - 8 caracteres, solo números (CIPNA)
      line += padField(row.FIPACIENTE || '', 8, 'left');

      // FICIAP - 6 caracteres, alfanuméricos
      line += padField(row.FICIAP || '000000', 6, 'left');

      // FIMEDICA - 6 caracteres, solo números
      line += padField(row.FIMEDICA || '', 6, 'right', '0');

      // FIMPORTEC - 10 caracteres, número decimal (7 enteros, 2 decimales) formato: 0000000.00
      line += formatDecimal(row.FIMPORTECIVA || 0, 10, 2);

      // FIUNIENV - 5 caracteres, solo números enteros de 1-99999
      line += padField(row.FIUNIENV || '', 5, 'right', '0');

      // FIUNIDIA - 6 caracteres, número decimal (3 enteros, 2 decimales) formato: 000.00
      line += formatDecimal(row.FIUNIDIA || 0, 6, 2);

      // FIDIAS - 2 caracteres, solo números enteros de 1-39
      line += padField(row.FIDIAS || '', 2, 'right', '0');

      // FIMPORTEF - 10 caracteres, número decimal (7 enteros, 2 decimales) formato: 0000000.00
      line += formatDecimal(row.FIMPORTEF || 0, 10, 2);

      // TIPOCOMPRA - 1 carácter, solo números: 1 o 2
      line += padField(row.TIPOCOMPRA || '1', 1, 'left');

      // NFACTURA - 20 caracteres, alfanuméricos
      // line += padField(row.NFACTURA || '00000000000000000000', 20, 'left');
      line += padField('00000000000000000000', 20, 'left');

      txtContent += line + '\n';
    });

    this.downloadCSV(txtContent);
  }

  downloadCSV(csvContent: string): void {
    // Crear un blob con el contenido CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Crear un enlace temporal para la descarga
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      // Generar nombre del archivo con fecha actual
      const fromDate = this.form.controls['fromDate'].value.format('YYYY-MM-DD');
      const toDate = this.form.controls['toDate'].value.format('YYYY-MM-DD');
      const institutionName = this.form.controls['selectedInstitution'].value?.shortName || 'institucion';
      const filename = `reporte-facturacion-${institutionName}-${fromDate}_${toDate}.txt`;
      
      this.logger.logAction(UserAction.Click, {
        message: 'Exportar TXT',
        file: `reporte-facturacion-${institutionName}-${fromDate}_${toDate}.txt`,
        records: this.dataSource.data.length
      });

      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar el objeto URL
      URL.revokeObjectURL(url);
    }
  }

  requestData(): void {
    this.loadingService.start('Obteniendo reporte...');

    const params: any = {
      institutionList: this.form.controls['selectedInstitution'].value.id.toString(),
      dateFrom: Utils.BUG_FixDateForTimeZone(this.form.controls['fromDate'].value.toDate())?.toISOString().split(".")[0],
      dateUntil: Utils.BUG_FixDateForTimeZone(this.form.controls['toDate'].value.toDate())?.toISOString().split(".")[0]
    };

    this.logger.logAction(UserAction.Click, {
      message: 'Obtener datos',
      ...params
    });

    this.reportService.getReportData({
      report: 'ClinicBilling',
      params: params,
    }).subscribe({
      next: (response: ReportClinicBillingResponse) => {
        const visibleData = response.filter(item => 
          !item.AtHome &&
          !item.ForcedPayment &&
          !item.IsPrivate && 
          !item.MutuasMuface &&
          !item.OutsidePharma &&
          !!item.RecurrencePattern &&
          !!item.PayMed
        );
        this.hiddenData = response.filter(item =>
          item.AtHome ||
          item.ForcedPayment ||
          item.IsPrivate ||
          item.MutuasMuface ||
          item.OutsidePharma ||
          !item.RecurrencePattern ||
          !item.PayMed
        );

        this.dataSource.data = visibleData;
        this.createTableFormControls(visibleData as any);
        this.dataRequested = true;
        this.cdRef.detectChanges();
        this.dataSource.sort = this.sort;
      },
      error: (error) => {
        this.loadingService.stop();
        this.calls.openSnack('Error al obtener el reporte');
      },
      complete: () => {
        this.loadingService.stop();
      }
    });
  }

  onCipnaChange(element: any, index: number): void {
    // La sincronización automática ya maneja esto
  }

  onCieChange(element: any, index: number): void {
    // La sincronización automática ya maneja esto
  }

  onCodigoNacionalChange(element: any, index: number): void {
    // La sincronización automática ya maneja esto
  }

  onPrecioChange(element: any, index: number): void {
    // Validación de precio válido (mayor que 0)
    const control = this.getFieldControl(element, 'FIMPORTEC');
    const value = control?.value;

    if (value < 0) {
      control?.setValue(0);
      return;
    }

    // Actualizar FIMPORTEC_LAB o FIMPORTEC_DIST según TIPOCOMPRA
    const formGroup = this.getRowFormGroup(element);
    const tipoCompra = formGroup?.get('TIPOCOMPRA')?.value;

    if (tipoCompra == 1) {
      // Si es Lab, actualizar FIMPORTEC_LAB
      formGroup?.get('FIMPORTEC_LAB')?.setValue(value, { emitEvent: false });
      element.FIMPORTEC_LAB = value;
    } else if (tipoCompra == 2) {
      // Si es Dist, actualizar FIMPORTEC_DIST
      formGroup?.get('FIMPORTEC_DIST')?.setValue(value, { emitEvent: false });
      element.FIMPORTEC_DIST = value;
    }
  }

  onUnidadesEnvaseChange(element: any, index: number): void {
    // Validación para números enteros positivos
    const control = this.getFieldControl(element, 'FIUNIENV');
    const value = control?.value;
    
    if (value < 0) {
      control?.setValue(0);
    }
  }

  onUnidadesDiaConsumidasChange(element: any, index: number): void {
    // Validación para números enteros positivos
    const control = this.getFieldControl(element, 'FIUNIDIA');
    const value = control?.value;
    
    if (value < 0) {
      control?.setValue(0);
    }
  }

  onDiasTratamientoChange(element: any, index: number): void {
    // Validación para números enteros positivos
    const control = this.getFieldControl(element, 'FIDIAS');
    const value = control?.value;
    
    if (value < 0) {
      control?.setValue(0);
    }
  }

  onImporteFacturadoChange(element: any, index: number): void {
    // Validación de importe válido (mayor que 0)
    const control = this.getFieldControl(element, 'FIMPORTEF');
    const value = control?.value;
    
    if (value < 0) {
      control?.setValue(0);
    }
  }

  onTipoCompraChange(element: any, index: number): void {
    // La sincronización automática ya maneja esto
  }

  onFacturaChange(element: any, index: number): void {
    // Validación de formato de factura si es necesario
  }

  /**
   * Crea los controles de formulario para cada fila de la tabla
   */
  createTableFormControls(data: ReportClinicBillingItem[]): void {
    // Limpiar el FormArray actual y el mapa
    this.tableFormArray.clear();
    this.rowFormMap.clear();

    let index = 0;

    // Crear un FormGroup por cada elemento de datos
    for (const item of data) {
      // Crear un ID único para cada fila
      const rowId = this.generateRowId(item, index);
      item._rowId = rowId;

      // Calcular FIMPORTEC según TIPOCOMPRA
      const tipoCompra = item.TIPOCOMPRA || 1;
      const fimportec = tipoCompra == 1 ? (item.FIMPORTEC_LAB || 0) : (item.FIMPORTEC_DIST || 0);
      const fimporteciva = Math.round(fimportec * (1 + (item.IVA || 0) / 100) * 100) / 100;
      const fimportef = Math.round(((fimporteciva / item.FIUNIENV) * item.FIUNIDIA * item.FIDIAS) * 100) / 100;

      const rowFormGroup = this.formBuilder.group({
        FIPACIENTE: [item.FIPACIENTE || ''],
        FICIAP: [item.FICIAP || ''],
        FIMEDICA: [item.FIMEDICA || 0],
        FIMPORTEC: [fimportec],
        FIMPORTEC_LAB: [item.FIMPORTEC_LAB || 0],
        FIMPORTEC_DIST: [item.FIMPORTEC_DIST || 0],
        FIMPORTECIVA: [fimportec ? fimporteciva : 0],
        FIUNIENV: [item.FIUNIENV || 0],
        FIUNIDIA: [item.FIUNIDIA || 0],
        FIDIAS: [item.FIDIAS || 0],
        IVA: [item.IVA || 0],
        FIMPORTEF: [fimportef || 0],
        TIPOCOMPRA: [tipoCompra]
      });
      
      // Guardar en el mapa la correspondencia
      this.rowFormMap.set(rowId, rowFormGroup);
      
      // Suscribirse a los cambios de valor para mantener sincronización automática
      this.subscribeToFormChanges(rowFormGroup, item);
      
      this.tableFormArray.push(rowFormGroup);

      index++;
    };
  }

  /**
   * Genera un ID único para cada fila
   */
  generateRowId(item: any, index: number): string {
    // Usar una combinación de campos únicos o el índice como fallback
    return `row_${item.FIPACIENTE || ''}_${item.FICIAP || ''}_${index}_${Date.now()}`;
  }

  /**
   * Función trackBy para la tabla
   */
  trackByRowId(index: number, item: any): string {
    return item._rowId || index;
  }

  /**
   * Suscribe a los cambios de un FormGroup específico para mantener sincronización
   */
  subscribeToFormChanges(formGroup: FormGroup, item: any): void {
    console.log('SUBSCRIBE')
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key)?.valueChanges.subscribe(value => {
        // Actualizar directamente el elemento referenciado
        item[key] = value;
      });
    });

    // Actualizar FIMPORTEC cuando cambia TIPOCOMPRA
    formGroup.get('TIPOCOMPRA')?.valueChanges.subscribe(() => {
      this.updateFIMPORTECByTipoCompra(formGroup, item);
    });

    // Recalcular FIMPORTEF cuando cambian los campos de la fórmula
    const fieldsToWatchFIMPORTEF = ['FIMPORTECIVA', 'FIUNIENV', 'FIUNIDIA', 'FIDIAS'];

    fieldsToWatchFIMPORTEF.forEach(field => {
      formGroup.get(field)?.valueChanges.subscribe(() => {
        this.recalculateFIMPORTEF(formGroup, item);
      });
    });

    // Recalcular FIMPORTECIVA cuando cambian FIMPORTEC o IVA
    const fieldsToWatchFIMPORTECIVA = ['FIMPORTEC', 'IVA'];

    fieldsToWatchFIMPORTECIVA.forEach(field => {
      formGroup.get(field)?.valueChanges.subscribe(() => {
        console.log('CHANGE')
        this.recalculateFIMPORTECIVA(formGroup, item);
      });
    });

    // Actualizar FIMPORTEC_LAB o FIMPORTEC_DIST cuando cambia FIMPORTEC
    formGroup.get('FIMPORTEC')?.valueChanges.subscribe((value) => {
      const tipoCompra = formGroup.get('TIPOCOMPRA')?.value;

      if (tipoCompra == 1) {
        // Si es Lab, actualizar FIMPORTEC_LAB
        formGroup.get('FIMPORTEC_LAB')?.setValue(value, { emitEvent: false });
        item.FIMPORTEC_LAB = value;
      } else if (tipoCompra == 2) {
        // Si es Dist, actualizar FIMPORTEC_DIST
        formGroup.get('FIMPORTEC_DIST')?.setValue(value, { emitEvent: false });
        item.FIMPORTEC_DIST = value;
      }
    });
  }

  /**
   * Recalcula FIMPORTEF usando la fórmula: FIMPORTEF = (FIMPORTEC / FIUNIENV) * FIUNIDIA * FIDIAS
   */
  recalculateFIMPORTEF(formGroup: FormGroup, item: any): void {
    const fimporteciva = formGroup.get('FIMPORTECIVA')?.value || 0;
    const fiunienv = formGroup.get('FIUNIENV')?.value || 0;
    const fiunidia = formGroup.get('FIUNIDIA')?.value || 0;
    const fidias = formGroup.get('FIDIAS')?.value || 0;

    // Evitar división por cero
    if (fiunienv === 0) {
      formGroup.get('FIMPORTEF')?.setValue(0, { emitEvent: false });
      item.FIMPORTEF = 0;
      return;
    }

    // Calcular FIMPORTEF con la fórmula y redondear a 2 decimales
    const fimportef = Math.round(((fimporteciva / fiunienv) * fiunidia * fidias) * 100) / 100;

    // Actualizar el FormControl sin emitir evento para evitar loops
    formGroup.get('FIMPORTEF')?.setValue(fimportef, { emitEvent: false });

    // Actualizar el item directamente
    item.FIMPORTEF = fimportef;
  }

  /**
   * Recalcula FIMPORTECIVA usando la fórmula: FIMPORTECIVA = FIMPORTEC * (1 + IVA/100)
   */
  recalculateFIMPORTECIVA(formGroup: FormGroup, item: any): void {
    const fimportec = formGroup.get('FIMPORTEC')?.value || 0;
    const iva = formGroup.get('IVA')?.value || 0;

    // Calcular FIMPORTECIVA con la fórmula y redondear a 2 decimales
    const precioIVA = Math.round(fimportec * (1 + iva / 100) * 100) / 100;

    // Actualizar el FormControl sin emitir evento para evitar loops
    formGroup.get('FIMPORTECIVA')?.setValue(precioIVA, { emitEvent: true });

    // Actualizar el item directamente
    item.FIMPORTECIVA = precioIVA;
  }

  /**
   * Actualiza FIMPORTEC según el TIPOCOMPRA seleccionado
   * Si TIPOCOMPRA = 1 (Lab) -> FIMPORTEC = FIMPORTEC_LAB
   * Si TIPOCOMPRA = 2 (Dist) -> FIMPORTEC = FIMPORTEC_DIST
   */
  updateFIMPORTECByTipoCompra(formGroup: FormGroup, item: any): void {
    const tipoCompra = formGroup.get('TIPOCOMPRA')?.value;
    const fimportecLab = formGroup.get('FIMPORTEC_LAB')?.value || 0;
    const fimportecDist = formGroup.get('FIMPORTEC_DIST')?.value || 0;

    // Seleccionar el precio según el tipo de compra
    const nuevoFimportec = tipoCompra == 1 ? fimportecLab : fimportecDist;

    // Actualizar FIMPORTEC
    formGroup.get('FIMPORTEC')?.setValue(nuevoFimportec, { emitEvent: true });

    // Actualizar el item directamente
    item.FIMPORTEC = nuevoFimportec;
  }

  /**
   * Obtiene el FormGroup de una fila específica por elemento
   */
  getRowFormGroup(element: any): FormGroup {
    return this.rowFormMap.get(element._rowId) as FormGroup;
  }

  /**
   * Obtiene el FormControl de un campo específico en una fila
   */
  getFieldControl(element: any, fieldName: string): FormControl {
    const formGroup = this.getRowFormGroup(element);
    return formGroup?.get(fieldName) as FormControl;
  }

  /**
   * Obtiene el valor de un campo específico en una fila
   */
  getFieldValue(element: any, fieldName: string): any {
    const formGroup = this.getRowFormGroup(element);
    return formGroup?.get(fieldName)?.value;
  }

  /**
   * Sincroniza los datos del formulario con el dataSource
   */
  syncFormWithDataSource(): void {
    this.tableFormArray.controls.forEach((control: any, index: number) => {
      const formValue = control.value;
      const dataItem = this.dataSource.data[index];
      
      if (dataItem && formValue) {
        Object.keys(formValue).forEach(key => {
          (dataItem as any)[key] = formValue[key];
        });
      }
    });
  }

  onDeleteRow(element: any, index: number): void {
    // Encontrar el índice real del elemento en el array completo usando el _rowId
    const actualIndex = this.dataSource.data.findIndex(item => item._rowId === element._rowId);

    if (actualIndex === -1) {
      console.error('No se pudo encontrar el elemento para eliminar');
      return;
    }

    // Eliminar del FormArray usando el índice real
    this.tableFormArray.removeAt(actualIndex);

    // Eliminar del mapa de formularios usando el ID único
    if (element._rowId) {
      this.rowFormMap.delete(element._rowId);
    }

    // Crear una nueva copia del array sin el elemento eliminado
    const newData = [...this.dataSource.data];
    newData.splice(actualIndex, 1);
    this.dataSource.data = newData;

    // Forzar detección de cambios
    this.cdRef.detectChanges();
  }

  openAddHiddenDataDialog(): void {
    const dialogRef = this.dialog.open(AddHiddenDataDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      data: {
        hiddenData: this.hiddenData
      }
    });

    dialogRef.afterClosed().subscribe((selectedItems: ReportClinicBillingItem[]) => {
      if (selectedItems && selectedItems.length > 0) {
        // Añadir los elementos seleccionados a dataSource.data
        const currentData = [...this.dataSource.data];
        const newData = [...currentData, ...selectedItems];

        // Actualizar dataSource
        this.dataSource.data = newData;

        // Crear controles de formulario para los nuevos elementos
        selectedItems.forEach((item, index) => {
          // Generar un ID único para cada fila
          const rowId = this.generateRowId(item, currentData.length + index);
          item._rowId = rowId;

          // Calcular FIMPORTEC según TIPOCOMPRA
          const tipoCompra = item.TIPOCOMPRA || 1;
          const fimportec = tipoCompra == 1 ? (item.FIMPORTEC_LAB || 0) : (item.FIMPORTEC_DIST || 0);
          const fimporteciva = Math.round(fimportec * (1 + (item.IVA || 0) / 100) * 100) / 100;
          const fimportef = Math.round(((fimporteciva / item.FIUNIENV) * item.FIUNIDIA * item.FIDIAS) * 100) / 100;

          const rowFormGroup = this.formBuilder.group({
            FIPACIENTE: [item.FIPACIENTE || ''],
            FICIAP: [item.FICIAP || ''],
            FIMEDICA: [item.FIMEDICA || 0],
            FIMPORTEC: [fimportec],
            FIMPORTEC_LAB: [item.FIMPORTEC_LAB || 0],
            FIMPORTEC_DIST: [item.FIMPORTEC_DIST || 0],
            FIMPORTECIVA: [fimportec ? fimporteciva : 0],
            FIUNIENV: [item.FIUNIENV || 0],
            FIUNIDIA: [item.FIUNIDIA || 0],
            FIDIAS: [item.FIDIAS || 0],
            IVA: [item.IVA || 0],
            FIMPORTEF: [fimportef || 0],
            TIPOCOMPRA: [tipoCompra]
          });

          // Guardar en el mapa la correspondencia
          this.rowFormMap.set(rowId, rowFormGroup);

          // Suscribirse a los cambios de valor para mantener sincronización automática
          this.subscribeToFormChanges(rowFormGroup, item);

          this.tableFormArray.push(rowFormGroup);
        });

        // Eliminar los elementos seleccionados de hiddenData
        this.hiddenData = this.hiddenData.filter(
          item => !selectedItems.includes(item)
        );

        // Forzar detección de cambios
        this.cdRef.detectChanges();

        // Mostrar mensaje de éxito
        this.calls.openSnack(
          `Se han añadido ${selectedItems.length} elemento${selectedItems.length !== 1 ? 's' : ''} correctamente`
        );
      }
    });
  }

  handleRestoreData(savedData: any): void {
    if (!savedData) {
      this.calls.openSnack('No se encontraron datos para restaurar');
      return;
    }

    try {
      // Restaurar datos del formulario
      if (savedData.formData) {
        const { selectedInstitution, fromDate, toDate, displayMode } = savedData.formData;

        // Restaurar institución si existe en las opciones
        if (selectedInstitution) {
          // Buscar la institución en las opciones disponibles por ID para usar la misma referencia
          const institution = this.selectOptionsInsitutions.find(
            opt => opt.value?.id === selectedInstitution.id
          );
          if (institution) {
            this.form.controls['selectedInstitution'].setValue(institution.value);
          }
        }

        // Restaurar fechas
        if (fromDate) {
          this.form.controls['fromDate'].setValue(moment(fromDate));
        }
        if (toDate) {
          this.form.controls['toDate'].setValue(moment(toDate));
        }

        // Restaurar modo de visualización
        if (displayMode !== undefined) {
          this.form.controls['displayMode'].setValue(displayMode);
        }
      }

      // Restaurar datos de la tabla
      if (savedData.tableData && Array.isArray(savedData.tableData)) {
        this.dataSource.data = savedData.tableData;
        this.createTableFormControls(savedData.tableData);
        this.dataRequested = true;
      }

      // Restaurar datos ocultos
      if (savedData.hiddenData && Array.isArray(savedData.hiddenData)) {
        this.hiddenData = savedData.hiddenData;
      }

      this.cdRef.detectChanges();
      this.dataSource.sort = this.sort;

      this.calls.openSnack('Datos restaurados correctamente');
    } catch (error) {
      console.error('Error al restaurar los datos:', error);
      this.calls.openSnack('Error al restaurar los datos');
    }
  }
}
